// The Library's uploads: each chosen or dropped file is a row that moves through its own states. The queue lives
// outside React so an upload goes on while the person is elsewhere in the app; the Library page reads it back.
// Bytes go through the one upload path (uploadWorkspaceFile); the server still owns scope, admission and extraction.
import { useSyncExternalStore } from 'react';
import { MAX_UPLOAD_BYTES, uploadErrorMessage, uploadMime } from '../api';
import { notifyLibraryChanged } from '../artifacts/library-changed';
import { UploadConnectionLost } from '../direct-upload';
import { uploadWorkspaceFile, WorkspaceUploadRefused } from '../file-upload';
import type { CompanyFile } from '../types';
import { fileExtension } from './upload-format';

/** Why a row cannot go on: refused before sending (type, size, empty), or refused by the server. */
export type LibraryUploadProblem =
  /** `modern`: the Office format to save an older one as first (.doc → .docx). */
  | { kind: 'type'; extension: string; modern?: string }
  | { kind: 'size'; bytes: number }
  | { kind: 'empty' }
  | { kind: 'failed'; message: string };

export type LibraryUploadStatus =
  | 'waiting'
  | 'uploading'
  | 'finishing'
  | 'paused'
  | 'problem'
  | 'done';

export interface LibraryUpload {
  key: string;
  name: string;
  size: number;
  status: LibraryUploadStatus;
  /** Bytes sent so far. */
  loaded: number;
  /** Seconds left at the current pace; null until there is a pace to go by. */
  secondsLeft: number | null;
  problem: LibraryUploadProblem | null;
  /** The saved file, once the server has it. */
  file: CompanyFile | null;
  /** When the row settled into a saved file (ms), for its brief arrival tint. */
  finishedAt: number | null;
}

type Entry = LibraryUpload & {
  source: File | null;
  /** Resumes the queue has started on its own since the last success, Retry or reconnection. */
  autoResumes: number;
  /** Discards the session the server holds for this row (set once it has one). */
  discard: (() => Promise<void>) | null;
  controller: AbortController | null;
  startedAt: number;
  sampledAt: number;
  sampledLoaded: number;
  rate: number | null;
};

/**
 * Files on their way at once. They share the person's bandwidth; the server allows one more session than this
 * (MAX_ACTIVE_UPLOADS_PER_PERSON in src/company-workspace/storage/contract.mjs), so a chat attachment or a meeting
 * recording still goes while the Library sends.
 */
const PARALLEL = 3;
const SAMPLE_MS = 250;
const EMIT_MS = 100;
const RESUME_FIRST_MS = 5_000;
const RESUME_MAX_MS = 60_000;
/** After this many quiet resumes a paused row waits for Retry or the browser coming back online. */
const AUTO_RESUMES = 5;
/** Other uploads (another tab, chat attachments, meetings) hold the person's sessions: wait, then try again. */
const BUSY_FIRST_MS = 3_000;
const BUSY_TRIES = 8;

let entries: Entry[] = [];
let snapshot: readonly LibraryUpload[] = [];
let scope = '';
let counter = 0;
let emitTimer: ReturnType<typeof setTimeout> | null = null;
let resumeTimer: ReturnType<typeof setTimeout> | null = null;
let resumeDelay = RESUME_FIRST_MS;
let wakeTimer: ReturnType<typeof setTimeout> | null = null;
/** While other uploads hold the person's sessions, the queue starts nothing new until this time (ms). */
let busyUntil = 0;
let busyTries = 0;
let listening = false;
let guarding = false;
let savedSinceSettled = false;
const listeners = new Set<() => void>();
const settledListeners = new Set<() => void>();

const active = (entry: Entry) =>
  entry.status === 'waiting' || entry.status === 'uploading' || entry.status === 'finishing';
/** The same file chosen twice would share one server session, so its copies go one after another. */
const sameFile = (a: Entry, b: Entry) =>
  Boolean(a.source && b.source) &&
  a.source?.name === b.source?.name &&
  a.source?.size === b.source?.size &&
  a.source?.type === b.source?.type;
const busy = (error: unknown) =>
  error instanceof WorkspaceUploadRefused && error.code === 'workspace_upload_busy';

const view = ({
  key,
  name,
  size,
  status,
  loaded,
  secondsLeft,
  problem,
  file,
  finishedAt,
}: Entry): LibraryUpload => ({
  key,
  name,
  size,
  status,
  loaded,
  secondsLeft,
  problem,
  file,
  finishedAt,
});

function emit() {
  if (emitTimer) clearTimeout(emitTimer);
  emitTimer = null;
  snapshot = entries.map(view);
  for (const listener of listeners) listener();
}

/** Progress arrives many times a second; the rows redraw at most every EMIT_MS. */
function emitSoon() {
  emitTimer ??= setTimeout(emit, EMIT_MS);
}

function patch(key: string, change: Partial<Entry>) {
  entries = entries.map((entry) => (entry.key === key ? { ...entry, ...change } : entry));
}

const find = (key: string) => entries.find((entry) => entry.key === key);

/** Older Office formats the row asks to save in their current form first. */
const MODERN_OFFICE: Readonly<Record<string, string>> = { DOC: 'docx', XLS: 'xlsx', PPT: 'pptx' };

/** Refused before anything is sent: an unsupported type, over the limit, or empty. */
function admission(file: File): LibraryUploadProblem | null {
  const extension = fileExtension(file.name);
  if (!uploadMime(file.name))
    return Object.hasOwn(MODERN_OFFICE, extension)
      ? { kind: 'type', extension, modern: MODERN_OFFICE[extension] }
      : { kind: 'type', extension };
  if (file.size > MAX_UPLOAD_BYTES) return { kind: 'size', bytes: file.size };
  if (file.size === 0) return { kind: 'empty' };
  return null;
}

function progress(key: string, fraction: number) {
  const entry = find(key);
  if (!entry || entry.status !== 'uploading') return;
  const loaded = Math.round(Math.min(1, Math.max(0, fraction)) * entry.size);
  const now = Date.now();
  const change: Partial<Entry> = { loaded };
  if (now - entry.sampledAt >= SAMPLE_MS) {
    const instant = ((loaded - entry.sampledLoaded) * 1000) / (now - entry.sampledAt);
    const rate = entry.rate === null ? instant : entry.rate * 0.7 + instant * 0.3;
    Object.assign(change, {
      rate,
      sampledAt: now,
      sampledLoaded: loaded,
      // A pace needs a second of sending before it says anything.
      secondsLeft: rate > 0 && now - entry.startedAt >= 1000 ? (entry.size - loaded) / rate : null,
    });
  }
  // Every byte is with the server: it checks and reads the file before the row settles.
  if (fraction >= 1) Object.assign(change, { status: 'finishing', secondsLeft: null });
  patch(key, change);
  if (fraction >= 1) emit();
  else emitSoon();
}

const connectionLost = (error: unknown) =>
  error instanceof UploadConnectionLost ||
  (typeof navigator !== 'undefined' && navigator.onLine === false);

/** The row's words for a refusal: the server's own reason, or a plain one for anything unexpected. */
const refusal = (name: string, error: unknown) =>
  error instanceof Error && !(error instanceof TypeError) && error.message
    ? error.message
    : uploadErrorMessage(name, 0);

async function run(key: string) {
  const entry = find(key);
  if (!entry?.source) return;
  const controller = new AbortController();
  const now = Date.now();
  patch(key, {
    status: 'uploading',
    controller,
    problem: null,
    loaded: 0,
    secondsLeft: null,
    rate: null,
    startedAt: now,
    sampledAt: now,
    sampledLoaded: 0,
  });
  emit();
  const mine = () => find(key)?.controller === controller && !controller.signal.aborted;
  try {
    const file = await uploadWorkspaceFile(entry.source, false, {
      signal: controller.signal,
      // A part still finishing after its run was stopped says nothing.
      onProgress: (fraction) => mine() && progress(key, fraction),
      onSession: (discard) => {
        if (mine()) patch(key, { discard });
      },
    });
    if (!mine()) return;
    resumeDelay = RESUME_FIRST_MS;
    busyTries = 0;
    savedSinceSettled = true;
    patch(key, {
      status: 'done',
      autoResumes: 0,
      discard: null,
      file,
      loaded: entry.size,
      secondsLeft: null,
      controller: null,
      source: null,
      finishedAt: Date.now(),
    });
    emit();
    notifyLibraryChanged();
  } catch (error) {
    if (!mine()) return;
    if (connectionLost(error)) {
      patch(key, { status: 'paused', controller: null, secondsLeft: null });
      scheduleResume();
    } else if (busy(error) && busyTries < BUSY_TRIES) {
      // Not a refusal of this file: the queue waits its turn, as the row says, and asks again later. Rows refused
      // together share one wait.
      if (busyUntil <= Date.now()) {
        busyUntil = Date.now() + Math.min(BUSY_FIRST_MS * 2 ** busyTries, 30_000);
        busyTries += 1;
      }
      patch(key, { status: 'waiting', controller: null, loaded: 0, secondsLeft: null });
    } else {
      if (busy(error)) busyTries = 0;
      patch(key, {
        status: 'problem',
        controller: null,
        secondsLeft: null,
        problem: { kind: 'failed', message: refusal(entry.name, error) },
      });
    }
    emit();
  } finally {
    pump();
  }
}

function pump() {
  const now = Date.now();
  if (wakeTimer) clearTimeout(wakeTimer);
  wakeTimer = null;
  // Each row on its way holds one of the person's sessions, and so does a paused or refused row that still has its
  // session (for its resume or Retry); the others wait for a free one. While other uploads hold the person's
  // sessions, nothing new starts. A row going back to its own session goes first, then the oldest (rows are listed
  // newest first).
  const sending = entries.filter(
    (entry) => entry.status === 'uploading' || entry.status === 'finishing',
  );
  const held = entries.filter(
    (entry) => (entry.status === 'paused' || entry.status === 'problem') && entry.discard,
  );
  let free = PARALLEL - sending.length - held.length;
  const next = [...entries]
    .reverse()
    .filter((entry) => entry.status === 'waiting')
    .sort((a, b) => Number(Boolean(b.discard)) - Number(Boolean(a.discard)));
  if (busyUntil > now && free > 0) wakeTimer = setTimeout(pump, busyUntil - now);
  else
    for (const entry of next) {
      if (free <= 0) break;
      // A copy on its way, or paused or refused with its session, holds the session this one would take over.
      if ([...sending, ...held].some((other) => sameFile(other, entry))) continue;
      free -= 1;
      sending.push(entry);
      void run(entry.key);
    }
  if (savedSinceSettled && !entries.some(active)) {
    savedSinceSettled = false;
    for (const listener of settledListeners) listener();
  }
}

const resumable = (entry: Entry) => entry.status === 'paused' && entry.autoResumes < AUTO_RESUMES;

/** The timer's resume: only rows that have not used up their quiet resumes. */
function resumePaused() {
  if (resumeTimer) clearTimeout(resumeTimer);
  resumeTimer = null;
  if (!entries.some(resumable)) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    scheduleResume();
    return;
  }
  entries = entries.map((entry) =>
    resumable(entry) ? { ...entry, status: 'waiting', autoResumes: entry.autoResumes + 1 } : entry,
  );
  emit();
  pump();
}

/** The browser is back online: every paused row goes on, with its quiet resumes renewed. */
function reconnected() {
  if (resumeTimer) clearTimeout(resumeTimer);
  resumeTimer = null;
  entries = entries.map((entry) =>
    entry.status === 'paused' ? { ...entry, status: 'waiting', autoResumes: 0 } : entry,
  );
  emit();
  pump();
}

/**
 * A paused row goes on by itself: at once when the browser is back online, else after a growing pause, a few times.
 * A connection that keeps failing while the browser says it is online (a blocked storage host, say) stops there and
 * waits for Retry.
 */
function scheduleResume() {
  if (!listening && typeof window !== 'undefined') {
    listening = true;
    window.addEventListener('online', reconnected);
  }
  if (resumeTimer || !entries.some(resumable)) return;
  resumeTimer = setTimeout(resumePaused, resumeDelay);
  resumeDelay = Math.min(resumeDelay * 2, RESUME_MAX_MS);
}

/** Leaving or reloading the page while a file is on its way asks first: the upload would stop. */
function guardLeaving() {
  if (guarding || typeof window === 'undefined') return;
  guarding = true;
  window.addEventListener('beforeunload', (event) => {
    // A paused row is still on its way: it goes on by itself.
    if (!entries.some((entry) => active(entry) || entry.status === 'paused')) return;
    event.preventDefault();
    event.returnValue = '';
  });
}

/**
 * Stops a row for good: its run is aborted, and the session the server holds for it is discarded (also while the
 * run is still re-reading its file, before it would send anything), so the person's next upload is not refused as
 * busy. A session the server is already promoting (after a failed completion) refuses the DELETE: it keeps the
 * session it holds until Retry completes it or the server lets it expire (24 h).
 */
function stop(entry: Entry) {
  entry.controller?.abort();
  void entry.discard?.();
}

export const libraryUploads = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  getSnapshot: () => snapshot,
  /** Called once the last running upload settles, when at least one file was saved since the last call. */
  onSettled(listener: () => void) {
    settledListeners.add(listener);
    return () => {
      settledListeners.delete(listener);
    };
  },
  /**
   * The company and person the uploads belong to. Another one (a company switch, a sign-out) cancels what is
   * running and clears every row, so nothing crosses over.
   */
  setScope(next: string) {
    if (next === scope) return;
    const previous = scope;
    scope = next;
    if (!previous) return;
    // The DELETEs go out under the new scope, so the old company's held session may simply expire; the
    // remembered resume keys are forgotten either way.
    for (const entry of entries) stop(entry);
    entries = [];
    busyUntil = 0;
    busyTries = 0;
    emit();
  },
  /** Each file becomes a row at the top, newest first; refused files show why in their own row. */
  add(files: Iterable<File>) {
    const added: Entry[] = [];
    for (const source of files) {
      const problem = admission(source);
      const now = Date.now();
      added.push({
        key: `upload-${++counter}`,
        name: source.name,
        size: source.size,
        status: problem ? 'problem' : 'waiting',
        loaded: 0,
        secondsLeft: null,
        problem,
        file: null,
        finishedAt: null,
        source: problem ? null : source,
        autoResumes: 0,
        discard: null,
        controller: null,
        startedAt: now,
        sampledAt: now,
        sampledLoaded: 0,
        rate: null,
      });
    }
    if (!added.length) return;
    entries = [...added.reverse(), ...entries];
    guardLeaving();
    emit();
    pump();
  },
  /** Cancelling or removing takes the row away; a transfer in flight is stopped and its session discarded. */
  remove(key: string) {
    const entry = find(key);
    if (!entry) return;
    entries = entries.filter((item) => item.key !== key);
    stop(entry);
    emit();
    pump();
  },
  retry(key: string) {
    const entry = find(key);
    if (!entry?.source || (entry.status !== 'paused' && entry.status !== 'problem')) return;
    patch(key, { status: 'waiting', problem: null, autoResumes: 0 });
    emit();
    pump();
  },
  /** Saved rows have been seen: the next visit lists them only as ordinary Library rows. */
  forgetFile(id: string) {
    const keep = entries.filter((entry) => entry.status !== 'done' || entry.file?.id !== id);
    if (keep.length === entries.length) return;
    entries = keep;
    emit();
  },
  forgetFinished() {
    if (!entries.some((entry) => entry.status === 'done')) return;
    entries = entries.filter((entry) => entry.status !== 'done');
    emit();
  },
};

/** Whose uploads these are: one company and one person. */
export const libraryUploadScope = (
  companyId: string | null | undefined,
  userId: string | null | undefined,
) => `${companyId ?? ''}:${userId ?? ''}`;

export function useLibraryUploads(): readonly LibraryUpload[] {
  return useSyncExternalStore(
    libraryUploads.subscribe,
    libraryUploads.getSnapshot,
    libraryUploads.getSnapshot,
  );
}
